import React, {useEffect, useMemo, useState} from 'react';
import {Button, ButtonGroup} from "reactstrap";
import {apiErrorRaised, useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import {Products} from "../../services/api/Products";
import {TableFilters} from "../TableFilters/TableFilters";
import {useTableFiltersState} from "../../context/table_filters/TableFiltersIndex";
import Paginator from "../Pagination/Paginator";
import ProductsTable from "./ProductsTable";
import {ProductsGrid} from "./ProductsGrid";
import {ColumnSelectorModal, useColumnSelector} from "../ColumnSelector";
import {useProjectState} from "../../context/project/ProjectIndex";
import useUrlFilters from "../../hooks/useUrlFilters";

// Define standard columns
const STANDARD_COLUMNS = [
  {id: 'image', name: 'Image', default: true, required: true},
  {id: 'title', name: 'Title', default: true},
  {id: 'status', name: 'Status', default: true, required: true},
  {id: 'sku', name: 'Sku', default: true},
  {id: 'price', name: 'Price', default: true},
  {id: 'sale_price', name: 'Sale Price', default: true},
  {id: 'brand', name: 'Brand', default: true},
  {id: 'category', name: 'Category', default: true},
  {id: 'id', name: 'Id', default: false},
  {id: 'url', name: 'Link', default: false, required: true},
  {id: 'preview', name: 'Preview', default: false},
  {id: 'availability', name: 'Availability', default: false},
  {id: 'gtin', name: 'GTIN', default: false},
  {id: 'mpn', name: 'MPN', default: false},
  {id: 'weight', name: 'Weight', default: false},
  {id: 'gender', name: 'Gender', default: false},
  {id: 'size', name: 'Size', default: false},
  {id: 'material', name: 'Material', default: false},
  {id: 'identifier_exists', name: 'Identifier Exists', default: false},
  {id: 'color', name: 'Color', default: false},
  {id: 'condition', name: 'Condition', default: false},
  {id: 'shipping', name: 'Shipping', default: false},
  {id: 'type', name: 'Type', default: false},
  {id: 'item_group_id', name: 'Item Group Id', default: false},
  {id: 'created', name: 'Updated On', default: false},
  {id: 'updated', name: 'Created On', default: false},
]

const TABLE_ID = 'products'

const ProductsList = ({companyId, projectId}) => {
  const dispatch = useApiErrorDispatch()
  const projectState = useProjectState();
  const [isLoading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [pagination, setPagination] = useState({})
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(30)
  const [isFiltersCollapsed, setFiltersCollapsed] = useState(true)
  const tableFilters = useTableFiltersState()
  const [layout, setLayout] = useState('table')
  const [renderedImage, setRenderedImage] = useState(false)

  // Combine standard columns with custom property columns
  const TABLE_COLUMNS = useMemo(() => {
    const customColumns = [];

    // Check if projectState has custom properties
    if (projectState && projectState.properties) {
      // Add each custom property as a column

      projectState.properties.forEach(propertyName => {
        customColumns.push({
          id: `custom_${propertyName}`,
          name: `Custom - ${propertyName.charAt(0).toUpperCase() + propertyName.slice(1)}`,
          default: false
        });
      });
    }

    return [...STANDARD_COLUMNS, ...customColumns];
  }, [projectState]);

  const {
    modalOpen,
    toggleModal,
    visibleColumns,
    handleSaveColumns,
    isColumnVisible
  } = useColumnSelector(TABLE_ID, TABLE_COLUMNS);

  // Helper to get column name by ID
  const getColumnName = (columnId) => {
    const column = TABLE_COLUMNS.find(col => col.id === columnId);
    return column ? column.name : columnId;
  };

  // Track whether the URL has been processed
  const [urlProcessed, setUrlProcessed] = useState(false);
  
  // Use our URL filters hook for both pagination and filters
  const {handlePaginationClick} = useUrlFilters({
    setOffset,
    setLimit,
    setFiltersCollapsed,
    defaultLimit: 30,
    onUrlProcessed: () => setUrlProcessed(true)
  });

  const toggleCollapsed = () => {
    setFiltersCollapsed(!isFiltersCollapsed)
  }

  // Wait for URL parameters to be processed before making API call
  useEffect(() => {
    // Skip API call until URL parameters are processed
    if (!urlProcessed) {
      return;
    }
    
    setLoading(true);
    Products.index(companyId, projectId, limit, offset, tableFilters).then((r) => {
      if (r?.status < 400) {
        setProducts(r.data?.products);
        setPagination(r.data?.pagination);
      } else {
        apiErrorRaised(dispatch, r?.response)
      }
      setLoading(false);
    });
  }, [dispatch, limit, offset, tableFilters, companyId, projectId, urlProcessed]);


  const handleToggle = () => {
    setRenderedImage(!renderedImage)
  };
  return (
    <>
      <div className={'view_header'}>
        <div className={'title'}>
          <h1>Products</h1>
        </div>
        <div className={'button_actions d-flex flex-column gap-3'}>
          <div>
            <Button outline color="primary"
                    onClick={toggleCollapsed}>
              <span className="fa-filter fa me-1"/>
              Filter
            </Button>
            <Button outline color="primary" onClick={toggleModal}>
              <span className="fa-columns fa me-1"/>
              Modify Columns
            </Button>
            <ButtonGroup aria-label="Settings" role="group">
              <Button className={'btn-icon-only'} outline color={'primary'} active={layout === 'table'}
                      onClick={() => setLayout('table')}>
                <span className={'fa-solid fa-table'}/>
              </Button>
              <Button className={'btn-icon-only'} outline color={'primary'} active={layout === 'grid'}
                      onClick={() => setLayout('grid')}>
                <span className={'fa-solid fa-grip'}/>
              </Button>
            </ButtonGroup>
          </div>
          {
            layout === 'grid' ? <div className={'d-flex flex-row ms-1 align-items-center gap-2'}>
              <p className='small m-0'>
                Rendered Image
              </p>
              <div>
                <label className="custom-toggle mr-1">
                  <input defaultChecked type="checkbox" checked={renderedImage} onChange={handleToggle}/>
                  <span
                    className="custom-toggle-slider rounded-circle"
                    data-label-off="No"
                    data-label-on="Yes"
                  />
                </label>
              </div>
            </div> : <></>
          }

        </div>
      </div>
      <div className={'mt-3'}>
        <TableFilters entity={'product'} isCollapsed={isFiltersCollapsed}/>
      </div>
      {
        layout === 'table' ?
          <ProductsTable
            products={products}
            isLoading={isLoading}
            visibleColumns={visibleColumns}
            getColumnName={getColumnName}
          />
          :
          <ProductsGrid products={products} renderedImage={renderedImage}/>
      }
      <Paginator
        pagination={pagination}
        handleClick={handlePaginationClick}
        offset={offset}
        limit={limit}
      />
      {/* Column selector modal */}
      <ColumnSelectorModal
        isOpen={modalOpen}
        toggle={toggleModal}
        tableId={TABLE_ID}
        availableColumns={TABLE_COLUMNS}
        onSave={handleSaveColumns}
      />
    </>
  )
}


export default ProductsList;
